import React from 'react'
import { Box, Grid, Paper, Typography } from '@mui/material'
import Chip from '@mui/material/Chip'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'

import { type KycArchiveViewProps } from './KycArchiveApplicationPage'
import {
  useGetKycArchiveHistoryQuery,
  useGetKycArchiveNotesQuery
} from '../../../api-services/kyc-archive/kycArchiveApi'
import LoadingComponent from '../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { KycArchiveAttachmentButton } from './KycArchiveAttachmentButton'
import { formatDateTimeAsApiFormat } from '../../../utility/formatters/DateFormatter'

const formatDateTime = (value: string | null): string => {
  if (value === null) return '-'
  return formatDateTimeAsApiFormat(new Date(value)) ?? '-'
}

const KycArchiveHistoryView: React.FC<KycArchiveViewProps> = ({
  application,
  applicationId
}: KycArchiveViewProps) => {
  const getHistoryApiResult = useGetKycArchiveHistoryQuery(applicationId)
  const getNotesApiResult = useGetKycArchiveNotesQuery(applicationId)

  const historyColumns: GridColDef[] = [
    {
      field: 'dateTime',
      headerName: 'Date',
      flex: 30,
      editable: false,
      valueFormatter: params => (params.value != null ? formatDateTimeAsApiFormat(new Date(params.value as string)) : '-')
    },
    { field: 'updateType', headerName: 'Update Type', flex: 25, editable: false },
    { field: 'state', headerName: 'State', flex: 18, editable: false },
    { field: 'stage', headerName: 'Stage', flex: 15, editable: false },
    { field: 'reviewer', headerName: 'Reviewer', flex: 30, editable: false },
    {
      field: 'historyNote',
      headerName: 'History Note',
      flex: 50,
      editable: false,
      renderCell: params => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', padding: '8px 0' }}>{params.value ?? '-'}</div>
      )
    },
    {
      field: 'txNote',
      headerName: 'Transaction Note',
      flex: 50,
      editable: false,
      renderCell: params => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', padding: '8px 0' }}>{params.value ?? '-'}</div>
      )
    }
  ]

  return (
    <>
      <Box sx={{ pb: 4 }}>
        <h3>History</h3>
        {getHistoryApiResult.isLoading && <LoadingComponent/>}
        {(getHistoryApiResult.error != null) &&
          <ErrorComponent message={RtkQueryHelper.mapResponseError(getHistoryApiResult.error)}/>}
        {getHistoryApiResult.isSuccess && application.isReconstructed && (
          <Typography variant={'body1'}>No review history - the application record was not included in the Acuant extract.</Typography>
        )}
        {getHistoryApiResult.isSuccess && !application.isReconstructed && (
          <Grid container spacing={4}>
            <Grid item md={12}>
              <DataGrid rows={getHistoryApiResult.data}
                        columns={historyColumns}
                        getRowId={row => row.historyEntryId}
                        autoHeight={true}
                        getRowHeight={() => 'auto'}
                        pageSize={25}
                        rowsPerPageOptions={[25]} />
            </Grid>
          </Grid>
        )}
      </Box>

      <Box sx={{ pb: 4 }}>
        <h3>Notes</h3>
        {getNotesApiResult.isLoading && <LoadingComponent/>}
        {(getNotesApiResult.error != null) &&
          <ErrorComponent message={RtkQueryHelper.mapResponseError(getNotesApiResult.error)}/>}
        {getNotesApiResult.isSuccess && getNotesApiResult.data.length === 0 && (
          <Typography variant={'body1'}>No notes recorded for this application.</Typography>
        )}
        {getNotesApiResult.isSuccess && getNotesApiResult.data.map(note => (
          <Paper key={note.noteId} variant={'outlined'} sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item md={12}>
                <Typography variant={'caption'} color={'text.secondary'}>
                  {formatDateTime(note.dateTime)}
                </Typography>
                {note.isManual && (
                  <Box sx={{ display: 'inline-block', ml: 2 }}>
                    <Chip label={'Manual'} size={'small'} variant={'outlined'} color={'primary'}/>
                  </Box>
                )}
              </Grid>
              <Grid item md={12}>
                <Typography variant={'body1'} sx={{ whiteSpace: 'pre-wrap' }}>{note.note ?? '-'}</Typography>
              </Grid>
              {note.attachments.length > 0 && (
                <Grid item md={12}>
                  {note.attachments.map(attachment => (
                    <Box key={attachment.id} sx={{ display: 'inline-block', mr: 2, mb: 1 }}>
                      <KycArchiveAttachmentButton attachment={attachment}/>
                    </Box>
                  ))}
                </Grid>
              )}
            </Grid>
          </Paper>
        ))}
      </Box>

      {/* ==================================================================================================== */}
    </>
  )
}

export default KycArchiveHistoryView
